import React, { useCallback, useEffect, useRef, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import DashboardCard from "../../components/DashboardComponents/DashboardCard"
import MiniStat, {
  MiniStatRow,
} from "../../components/DashboardComponents/MiniStat"
import { AvatarGroup } from "../../components/DashboardComponents/AvatarGroup"
import ProgressBar from "../../components/DashboardComponents/ProgressBar"
import CircularProgress from "../../components/Projects/CircularProgress"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import {
  RequestChangeCityModal,
  useAddOngoingRoleModal,
} from "../../components/Modals"
import EmptyState from "../../components/Common/EmptyState"
import DeleteConfirmation from "../../components/Common/DeleteConfirmation"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { setUser } from "../../store/slices/authSlice"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import {
  fetchProfileProjectSections,
  mapOngoingRole,
} from "./profileProjects"
import { fetchClassSummary, fetchSpellbookSummary, genomeConicGradient } from "./profileGenome"
import {
  fetchUserProfile,
  formatCityName,
  getAddressByType,
  getProfileCurrentRoles,
  getProfileDisplayName,
  getProfileRoleLabel,
  getProfileXp,
} from "./profileUser"
import {
  OngoingRoleSkeletonList,
  ProfileProjectSkeletonList,
} from "./ProfileProjectSkeleton"
import ProfilePhoto from "./ProfilePhoto"
import ArchivedProjectsListing from "./ArchivedProjectsListing"
import { deleteOngoingRole } from "../Authentication/Onboarding/onboardingOngoingRoleHelpers"

const PROJECT_SECTIONS = [
  { key: "past", title: "Past Projects", actionLabel: "Add Project" },
  { key: "present", title: "Present Projects", actionLabel: "Add Project" },
  {
    key: "future",
    title: "Future Projects (Private)",
    actionLabel: "Add Project",
  },
]

const genreSlug = (genre = "") =>
  String(genre || "other")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "other"

const GenomeLegend = ({ items, emptyTitle, emptyDescription }) => {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0)
  if (!items.length) {
    return (
      <EmptyState
        size="sm"
        title={emptyTitle}
        description={emptyDescription}
      />
    )
  }
  return (
    <ul className="my-profile__genome-legend">
      {items.map(item => (
        <li key={item.id || item.label}>
          <span
            className="my-profile__genome-dot"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          <span className="my-profile__genome-label">{item.label}</span>
          <span className="my-profile__genome-value">{Math.round(item.value)}%</span>
        </li>
      ))}
      <li className="my-profile__genome-total">
        <span className="my-profile__genome-label">Total</span>
        <span className="my-profile__genome-value">{Math.round(total)}%</span>
      </li>
    </ul>
  )
}

const GenrePill = ({ genre }) => (
  <span className={`my-profile__genre my-profile__genre--${genreSlug(genre)}`}>
    <span className="my-profile__genre-dot" aria-hidden="true" />
    {genre}
  </span>
)

const getProjectStatusTone = project => {
  if (project.completed) return "done"
  if (project.status === "Starting From") return "upcoming"
  return "progress"
}

const STATUS_RING_TONE = {
  done: "green",
  progress: "blue",
  upcoming: "orange",
}

const ProjectStatusCopy = ({ project, tone }) => {
  if (tone === "done") {
    return (
      <span className="my-profile__status my-profile__status--done">
        {project.status || "Completed"}
        {project.statusDate ? <small>{project.statusDate}</small> : null}
      </span>
    )
  }

  if (tone === "upcoming") {
    return (
      <span className="my-profile__status my-profile__status--upcoming">
        {project.status || "Starting From"}
        {project.statusDate ? <small>{project.statusDate}</small> : null}
      </span>
    )
  }

  const progressValue = Number(project.progress)
  const progress = Number.isFinite(progressValue)
    ? Math.min(100, Math.max(0, progressValue))
    : 0
  const progressLabel = progress.toFixed(1)

  return (
    <span className="my-profile__status my-profile__status--progress">
      {`${progressLabel}% Completed`}
      {project.daysLeft != null ? (
        <small>
          {project.daysLeft} {project.daysLeft === 1 ? "day" : "days"} left
        </small>
      ) : null}
    </span>
  )
}

const ProjectCard = ({ project, onView }) => {
  const tone = getProjectStatusTone(project)
  const progressValue = Number(project.progress)
  const progress = Number.isFinite(progressValue)
    ? Math.min(100, Math.max(0, progressValue))
    : 0
  // Present: hours %. Past: full ring. Future: empty ring.
  const ringValue =
    tone === "done" ? 100 : tone === "upcoming" ? 0 : progress

  return (
    <article className="my-profile__project-card">
      <div className="my-profile__project-main">
        <span
          className={`my-profile__code my-profile__code--${genreSlug(project.genre)}`}
        >
          {project.code}
        </span>
        <div className="my-profile__project-info">
          <div className="my-profile__project-title-row">
            <b className="my-profile__project-title">{project.title}</b>
            <GenrePill genre={project.genre} />
          </div>
          <p className="my-profile__project-meta">
            <span>{project.role || "—"}</span>
            <span className="my-profile__dot" aria-hidden="true">
              •
            </span>
            <span>{project.date || "—"}</span>
          </p>
        </div>
      </div>

      <div className="my-profile__project-avatars">
        <AvatarGroup
          people={project.collaborators || []}
          max={3}
          size="xs"
          total={project.totalCollaborators}
        />
      </div>

      <div className="my-profile__project-status">
        <div
          className={`my-profile__status-pill my-profile__status-pill--${tone}`}
        >
          <ProjectStatusCopy project={project} tone={tone} />
          <CircularProgress
            value={ringValue}
            max={100}
            tone={STATUS_RING_TONE[tone]}
            size={24}
            strokeWidth={3}
            className="my-profile__status-ring"
          />
        </div>
      </div>

      <CustomButton
        variant={project.completed ? "secondary" : "primary"}
        size="md"
        type="button"
        className="my-profile__project-cta"
        onClick={() => onView?.(project)}
      >
        View Project
      </CustomButton>
    </article>
  )
}

const ProfileSection = ({ title, actionLabel, onAction, children }) => (
  <section className="my-profile__block">
    <div className="my-profile__block-head">
      <h3 className="my-profile__block-title">{title}</h3>
      <CustomButton
        variant="secondary"
        size="sm"
        type="button"
        trailingIconSrc={ICONS.plus}
        trailingIconAlt=""
        className="my-profile__add-btn"
        onClick={onAction}
      >
        {actionLabel}
      </CustomButton>
    </div>
    {children}
  </section>
)

const MyProfile = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profileId } = useParams()
  const currentUser = useSelector(state => state.auth.currentUser)
  const userId = currentUser?.id
  const [isCityRequestOpen, setIsCityRequestOpen] = useState(false)
  const [projectsSummary, setProjectsSummary] = useState(null)
  const [sectionProjects, setSectionProjects] = useState({
    past: [],
    present: [],
    future: [],
  })
  const [isLoadingProjects, setIsLoadingProjects] = useState(true)
  const [profile, setProfile] = useState(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [ongoingRolesRaw, setOngoingRolesRaw] = useState([])
  const [isLoadingOngoingRoles, setIsLoadingOngoingRoles] = useState(true)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [isDeletingRole, setIsDeletingRole] = useState(false)
  const [artsClass, setArtsClass] = useState([])
  const [artsSpellbook, setArtsSpellbook] = useState([])
  const { openAddOngoingRole } = useAddOngoingRoleModal()
  const user = profile || currentUser
  const currentUserRef = useRef(currentUser)
  currentUserRef.current = currentUser

  useEffect(() => {
    document.title = "My Profile | AMOK . EARTH"
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadProjectsSummary = async () => {
      const summaryRes = await get(endpoints.getProjectsSummary)
      if (cancelled) return
      if (summaryRes?.data?.success) {
        setProjectsSummary(summaryRes.data.result)
      }
    }

    loadProjectsSummary()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!userId) return undefined
    let cancelled = false

    const loadProjects = async () => {
      setIsLoadingProjects(true)
      try {
        const grouped = await fetchProfileProjectSections({ userId })
        if (!cancelled) {
          setSectionProjects(grouped)
        }
      } finally {
        if (!cancelled) setIsLoadingProjects(false)
      }
    }

    loadProjects()
    return () => {
      cancelled = true
    }
  }, [userId])

  const loadProfile = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) return
      if (!silent) {
        setIsLoadingProfile(true)
        setIsLoadingOngoingRoles(true)
      }
      try {
        const nextProfile = await fetchUserProfile(userId)
        setProfile(nextProfile)
        setOngoingRolesRaw(getProfileCurrentRoles(nextProfile))
        if (nextProfile && nextProfile.id === userId) {
          dispatch(
            setUser({ ...currentUserRef.current, ...nextProfile }),
          )
        }
      } catch {
        setProfile(null)
        setOngoingRolesRaw([])
      } finally {
        if (!silent) {
          setIsLoadingProfile(false)
          setIsLoadingOngoingRoles(false)
        }
      }
    },
    [userId, dispatch],
  )

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const handleEditOngoingRole = useCallback(
    rawRole => {
      if (!rawRole?.id) return
      openAddOngoingRole({
        role: rawRole,
        onSuccess: () => loadProfile({ silent: true }),
      })
    },
    [openAddOngoingRole, loadProfile],
  )

  const handleDeleteOngoingRoleConfirm = useCallback(async () => {
    if (!roleToDelete?.id) return
    setIsDeletingRole(true)
    const deleted = await deleteOngoingRole(roleToDelete.id)
    setIsDeletingRole(false)
    if (!deleted) return
    setRoleToDelete(null)
    await loadProfile({ silent: true })
  }, [roleToDelete, loadProfile])

  useEffect(() => {
    if (!userId) return undefined
    let cancelled = false

    const loadGenome = async () => {
      const [classSummary, spellbookSummary] = await Promise.all([
        fetchClassSummary(userId),
        fetchSpellbookSummary(userId),
      ])
      if (!cancelled) {
        setArtsClass(classSummary)
        setArtsSpellbook(spellbookSummary)
      }
    }

    loadGenome()
    return () => {
      cancelled = true
    }
  }, [userId])

  const displayName = getProfileDisplayName(user, { fallback: "" })
  const avatarSrc = getUserAvatarUrl(user)
  const roleLabel = getProfileRoleLabel(user)
  const addresses = user?.addresses || []
  const primaryCity = formatCityName(getAddressByType(addresses, "primary"))
  const secondaryCity = formatCityName(getAddressByType(addresses, "secondary"))
  const tertiaryCity = formatCityName(getAddressByType(addresses, "tertiary"))
  const { xpValue, xpLevel, xpMax } = getProfileXp(user)
  const presentProjects = Number(projectsSummary?.present_count) || 0
  const pastProjects = Number(projectsSummary?.past_count) || 0
  const futureProjects = Number(projectsSummary?.future_count) || 0
  const totalProjects = presentProjects + pastProjects + futureProjects
  const listeningTo = String(user?.listening_to || "").trim()
  const reading = String(user?.reading || "").trim()
  const bioSubtitle = user?.bio || roleLabel

  if (profileId && !currentUser?.id) {
    return <Navigate to={`/public/profile/${profileId}`} replace />
  }

  if (
    profileId &&
    currentUser?.id &&
    String(profileId) !== String(currentUser.id)
  ) {
    return <Navigate to={`/public/profile/${profileId}`} replace />
  }

  return (
    <div className="page-content">
      <div className="my-profile">
        <PageHeader
          className="my-profile__header"
          title="My Profile"
          subtitle="Your public identity, creative roles, and the work you share on Amok."
          actions={
            <>
              <CustomButton
                variant="secondary"
                size="md"
                type="button"
                className="my-profile__header-btn"
                onClick={() => userId && navigate(`/public/profile/${userId}`)}
              >
                Public View
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                trailingIconSrc={ICONS.edit}
                trailingIconAlt=""
                onClick={() => userId && navigate(`/profile/edit/${userId}`)}
              >
                Edit Profile
              </CustomButton>
            </>
          }
        />

        <div className="my-profile__grid-row">
          <div className="my-profile__card my-profile__card--bio">
            <ProfilePhoto
              src={avatarSrc}
              name={displayName}
              loading={isLoadingProfile && !avatarSrc}
              className="my-profile__avatar"
            />
            <div className="my-profile__bio-details">
              <div className="my-profile__name-row">
                <h2 className="my-profile__name">
                  {displayName || (isLoadingProfile ? "" : "—")}
                </h2>
                {roleLabel ? (
                  <span className="my-profile__badge">{roleLabel}</span>
                ) : null}
              </div>
              <p className="my-profile__meta">
                <span>{primaryCity}</span>
                {bioSubtitle ? (
                  <>
                    <span className="my-profile__meta-sep">-</span>
                    <span>{bioSubtitle}</span>
                  </>
                ) : null}
              </p>
            </div>
          </div>

          <DashboardCard title="City" tone="red" topRight={primaryCity}>
            <MiniStatRow>
              <MiniStat label="Primary" value={primaryCity} color="red" />
              <MiniStat label="Secondary" value={secondaryCity} color="red" />
              <MiniStat label="Tertiary" value={tertiaryCity} color="red" />
            </MiniStatRow>
            <CustomButton
              variant="transparent"
              size="sm"
              type="button"
              fullWidth
              trailingIconSrc={ICONS.profileLocationTick}
              trailingIconAlt=""
              className="my-profile__city-btn"
              onClick={() => setIsCityRequestOpen(true)}
            >
              Request Change City
            </CustomButton>
          </DashboardCard>

          <div className="my-profile__card my-profile__card--spellbook">
            <div className="my-profile__card-head">
              <span className="my-profile__card-title">
                Arts Genome - Spellbook
              </span>
              {artsSpellbook.length ? (
                <span
                  className="my-profile__donut"
                  style={{
                    background: genomeConicGradient(artsSpellbook),
                  }}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <GenomeLegend
              items={artsSpellbook}
              emptyTitle="No Spellbook Data Yet"
              emptyDescription="Your arts genome spellbook will show up here."
            />
          </div>

          <div className="my-profile__card my-profile__card--class">
            <div className="my-profile__card-head">
              <span className="my-profile__card-title">
                Arts Genome - Class
              </span>
              {artsClass.length ? (
                <span
                  className="my-profile__donut"
                  style={{
                    background: genomeConicGradient(artsClass),
                  }}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <GenomeLegend
              items={artsClass}
              emptyTitle="No Class Data Yet"
              emptyDescription="Your arts genome class will show up here."
            />
          </div>
        </div>

        <div className="my-profile__grid-row">
          <DashboardCard
            title="XP Earned"
            tone="purple-custom"
            topRight={`Level ${xpLevel}`}
          >
            <div className="my-profile__xp-body">
              <div className="my-profile__xp-row">
                <span className="my-profile__xp-value">{`${xpValue}xp`}</span>
                <span className="my-profile__xp-max">{`${xpMax}xp`}</span>
              </div>
              <ProgressBar
                value={xpValue}
                max={xpMax}
                tone="purple"
                className="my-profile__xp-bar"
              />
            </div>
          </DashboardCard>

          <DashboardCard
            title="Total Projects"
            tone="darkgreen"
            topRight={totalProjects}
          >
            <MiniStatRow>
              <MiniStat
                label="Active"
                value={presentProjects}
                color="darkgreen"
              />
              <MiniStat label="Past" value={pastProjects} color="darkgreen" />
              <MiniStat
                label="Future"
                value={futureProjects}
                color="darkgreen"
              />
            </MiniStatRow>
          </DashboardCard>

          <div className="my-profile__card my-profile__card--listening">
            <div className="my-profile__media-head">
              <img src={ICONS.profileHeadphone} alt="" width={20} height={20} />
              <span className="my-profile__card-title">Listening to...</span>
            </div>
            {listeningTo ? (
              <p className="my-profile__media-value">{listeningTo}</p>
            ) : (
              <EmptyState
                size="sm"
                title="Nothing Here Yet"
                description="What you're listening to will show up here."
              />
            )}
          </div>

          <div className="my-profile__card my-profile__card--reading">
            <div className="my-profile__media-head">
              <img src={ICONS.profileBook} alt="" width={20} height={20} />
              <span className="my-profile__card-title">Reading...</span>
            </div>
            {reading ? (
              <p className="my-profile__media-value">{reading}</p>
            ) : (
              <EmptyState
                size="sm"
                title="Nothing Here Yet"
                description="What you're reading will show up here."
              />
            )}
          </div>
        </div>

        <ProfileSection
          title="Ongoing Roles"
          actionLabel="Add Role"
          onAction={() =>
            openAddOngoingRole({
              onSuccess: () => loadProfile({ silent: true }),
            })
          }
        >
          <div
            className="my-profile__project-list"
            aria-busy={isLoadingOngoingRoles || undefined}
            aria-live="polite"
          >
            {isLoadingOngoingRoles ? (
              <OngoingRoleSkeletonList />
            ) : ongoingRolesRaw.length === 0 ? (
              <EmptyState
                title="No Ongoing Roles Yet"
                description="Roles you add will show up here."
              />
            ) : (
              ongoingRolesRaw.map(rawRole => {
                const role = mapOngoingRole(rawRole)
                return (
                  <div key={role.id || rawRole.id} className="my-profile__ongoing-card">
                    <span
                      className={`my-profile__code my-profile__code--${genreSlug(role.genre)}`}
                    >
                      {role.code}
                    </span>
                    <div className="my-profile__ongoing-body">
                      <div className="my-profile__ongoing-left">
                        <b>{role.title}</b>
                        <div className="my-profile__genre-list">
                          {(role.genres || [role.genre]).map(genre => (
                            <GenrePill key={`${role.id}-${genre}`} genre={genre} />
                          ))}
                        </div>
                      </div>
                      <div className="my-profile__ongoing-meta">
                        <span>{role.role}</span>
                        <span className="my-profile__dot" aria-hidden="true">
                          •
                        </span>
                        <span>{role.date}</span>
                        <span className="my-profile__dot" aria-hidden="true">
                          •
                        </span>
                        <span>{role.duration}</span>
                      </div>
                    </div>
                    <div className="my-profile__ongoing-actions">
                      <CustomIconButton
                        iconSrc={ICONS.edit}
                        label="Edit role"
                        size="sm"
                        onClick={() => handleEditOngoingRole(rawRole)}
                      />
                      <CustomIconButton
                        iconSrc={ICONS.trash}
                        label="Delete role"
                        tone="danger"
                        size="sm"
                        onClick={() => setRoleToDelete(rawRole)}
                      />
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </ProfileSection>

        {PROJECT_SECTIONS.map(section => {
          const rows = sectionProjects[section.key] || []
          return (
            <ProfileSection
              key={section.key}
              title={section.title}
              actionLabel={section.actionLabel}
              onAction={() => navigate("/projects/create")}
            >
              <div
                className="my-profile__project-list"
                aria-busy={isLoadingProjects || undefined}
              >
                {isLoadingProjects ? (
                  <ProfileProjectSkeletonList />
                ) : rows.length === 0 ? (
                  <EmptyState
                    title="No Projects Yet"
                    description="Projects in this section will show up here."
                  />
                ) : (
                  rows.map(project => (
                    <ProjectCard
                      key={project.id || `${section.key}-${project.title}`}
                      project={project}
                      onView={() =>
                        project.id &&
                        navigate(`/projects/${project.id}/overview`)
                      }
                    />
                  ))
                )}
              </div>
            </ProfileSection>
          )
        })}

        <ArchivedProjectsListing />
      </div>

      <RequestChangeCityModal
        isOpen={isCityRequestOpen}
        onClose={() => setIsCityRequestOpen(false)}
        addresses={addresses}
      />

      <DeleteConfirmation
        isOpen={Boolean(roleToDelete)}
        onCancel={() => !isDeletingRole && setRoleToDelete(null)}
        onConfirm={handleDeleteOngoingRoleConfirm}
        title="Delete Ongoing Role"
        message={`Delete “${roleToDelete?.project_name || "this role"}”? This action cannot be undone.`}
        cancelText="Cancel"
        confirmText="Delete Role"
        confirmLoadingText="Deleting…"
        isDeleting={isDeletingRole}
      />
    </div>
  )
}

export default MyProfile
